import produce from 'immer';
import { handleActions } from 'redux-actions';
import actions from './actions';
import { updateStoreWithPath } from '../../../lib/common';
import { LIST_PAGINATION_MODEL } from './models';
import { COUPON_SEARCH_MODAL_MODEL } from '../coupon/models';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   * admin 계정 레벨
   */
  adminInfo: {
    id: null,
    name: null,
    email: null,
    phone: null,
    status: null,
    level: null,
  },
  /**
   *  스피너 토글
   */
  fullscreenLoader: false,

  /**
   *  사이드바 토글
   */
  sidebar: {
    isCollapsed: false,
  },

  /**
   *  이미지 보기 모달
   */
  imgViewModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  영상 업로드 모달
   */
  videoUploadModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  영상 보기 모달
   */
  videoViewModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  상품 상세 미리보기
   */
  productDetailPreviewModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  태그 미리보기 모달
   */
  tagPreviewModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  배송 조회 모달
   */
  deliveryTrackingModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  배송지 변경 이력 조회 모달
   */
  orderModifyDeliveryInfoModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  기획전 아이템 리스트 모달
   */
  eventItemListModal: {
    isOpen: false,
    data: {
      listData: {
        list: [],
        pagination: LIST_PAGINATION_MODEL,
      },
      eventId: null,
    },
  },

  /**
   *  상품 공지사항 아이템 리스트 모달
   */
  noticeProductItemListModal: {
    isOpen: false,
    data: {
      listData: {
        list: [],
        pagination: {
          pageSize: 100,
          pageNum: 0,
          pages: -1,
        },
      },
      productNoticeId: null,
    },
  },

  /**
   *  문자 발송 모달
   */
  sendMessageServiceModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  유저 상세에서 포인트 발급 모달
   */
  generatePointUserDetailModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  유저 상세에서 쿠폰 발급 모달
   */
  generateCouponUserDetailModal: {
    isOpen: false,
    data: {
      listData: COUPON_SEARCH_MODAL_MODEL,
    },
  },

  /**
   *  포인트 발급 유효성 검사 성공 모달
   */
  generatePointValidSuccessModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  포인트 발급 유효성 검사 실패 모달
   */
  generatePointValidFailModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  쿠폰 발급 유효성 검사 성공 모달
   */
  generateCouponValidSuccessModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  쿠폰 발급 유효성 검사 실패 모달
   */
  generateCouponValidFailModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  태그 거절 모달
   */
  tagRejectModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  태그 리스트 보기 모달
   */
  tagListViewModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  해당 브랜드에서 Product.status 에 따른 상품 리스트를 볼 수 있는 모달
   */
  brandProductListModal: {
    isOpen: false,
    data: {
      listData: {
        list: [],
        pagination: LIST_PAGINATION_MODEL,
      },
    },
  },

  /**
   *  반품, 교환 요청사유, 거부사유 모달
   */
  returnExchangeReasonModal: {
    isOpen: false,
    data: {
      attachments: [],
      title: null,
      reason: null,
    },
  },

  /**
   *  상품 2차 카테고리 보기 모달
   */
  productSubCategoryListViewModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  구매자 이전 구매내역 조회 모달
   */
  buyerPurchaseHistoryModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  상품 카테고리 검색 모달
   */
  searchProductCategoryModal: {
    isOpen: false,
    data: {},
  },
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  데이터 변경
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  스피너 토글
     */
    [actions.SET_FULLSCREEN_LOADER]: (state, action) => {
      return produce(state, (draft) => {
        draft.fullscreenLoader = action.payload;
      });
    },

    /**
     *  사이드바 토글
     */
    [actions.SIDEBAR_TOGGLE_COLLAPSE]: (state, action) => {
      return produce(state, (draft) => {
        draft.sidebar.isCollapsed = action.payload;
      });
    },

    /**
     *  기획전 아이템 리스트 모달 토글
     */
    [actions.OPEN_EVENT_ITEM_LIST_MODAL]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(['eventItemListModal', 'data', 'eventId'], action.payload.eventId, draft);
        updateStoreWithPath(['eventItemListModal', 'isOpen'], true, draft);
      });
    },

    /**
     *  기획전 아이템 리스트 받아오기
     */
    [actions.GET_EVENT_ITEM_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(['eventItemListModal', 'data', 'listData'], action.payload.listData, draft);
      });
    },

    /**
     *  홈 페이지 아이템 리스트 모달 토글
     */
    [actions.OPEN_HOME_ITEM_LIST_MODAL]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(['homeItemListModal', 'data', 'homeId'], action.payload.homeId, draft);
        updateStoreWithPath(['homeItemListModal', 'isOpen'], true, draft);
      });
    },

    /**
     *  상품 공지사항 아이템 리스트 모달 토글
     */
    [actions.OPEN_NOTICE_PRODUCT_ITEM_LIST_MODAL]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(
          ['noticeProductItemListModal', 'data', 'productNoticeId'],
          action.payload.productNoticeId,
          draft,
        );
        updateStoreWithPath(['noticeProductItemListModal', 'isOpen'], true, draft);
      });
    },

    /**
     *  상품 공지사항 아이템 리스트 받아오기
     */
    [actions.GET_NOTICE_PRODUCT_ITEM_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(['noticeProductItemListModal', 'data', 'listData'], action.payload.listData, draft);
      });
    },

    /**
     *  admin level 받아오기
     */
    [actions.GET_COMMON_DATA_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.adminInfo = action.payload.adminInfo;
      });
    },

    /**
     *  유저정보 스토어에 등록하기
     */
    [actions.FETCH_USER_DATA]: (state, action) => {
      return produce(state, (draft) => {
        draft.adminInfo = action.payload.adminInfo;
      });
    },

    /**
     *  [쿠폰 발급] 유저 상세 쿠폰 모달에서 발급할 쿠폰 리스트 요청하기
     */
    [actions.GET_ISSUED_COUPON_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  유저 상세 페이지에서 쿠폰 발급하는 모달에서 쿠폰 리스트를 보여주어야한다.
         */
        draft.generateCouponUserDetailModal.data.listData = action.payload.couponList;

        /**
         *  유저 상세 페이지에서 쿠폰 발급하는 모달에서 기본적으로 issuedCount 를 1로 초기화한다.
         */
        draft.generateCouponUserDetailModal.data.listData.list =
          draft.generateCouponUserDetailModal.data.listData.list.map((item) => {
            return { ...item, issuedCount: 1 };
          });
      });
    },

    /**
     *  브랜드 상품 리스트 요청하기
     */
    [actions.GET_BRAND_PRODUCT_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  해당 브랜드 상품 리스트
         */
        draft.brandProductListModal.data.listData = action.payload.listData;
      });
    },

    /**
     *  상품 카테고리 검색 리스트 요청하기 성공
     */
    [actions.GET_PRODUCT_CATEGORY_SEARCH_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        const productCategoryAllList = [];
        const productCategoryTreeList = [];
        let targetMainCategoryItem;
        let targetCategoryItem;
        [
          ...action.payload.exposeProductCategoryList.normal,
          ...action.payload.exposeProductCategoryList.emphasis,
        ].forEach((mainCategory) => {
          targetCategoryItem = {
            id: mainCategory.id,
            productCategoryId: mainCategory.id,
            productCategoryName: mainCategory.name,
            parentId: null,
          };

          productCategoryAllList.push(targetCategoryItem);
          targetMainCategoryItem = {
            ...targetCategoryItem,
            children: [],
          };

          if (mainCategory.subList && mainCategory.subList.length) {
            mainCategory.subList.forEach((subCategory) => {
              targetCategoryItem = {
                id: subCategory.id,
                productCategoryId: subCategory.id,
                productCategoryName: subCategory.name,
                parentId: mainCategory.id,
              };
              productCategoryAllList.push(targetCategoryItem);
              targetMainCategoryItem.children.push(targetCategoryItem);
            });
          }

          productCategoryTreeList.push(targetMainCategoryItem);
        });

        /**
         *  상품 카테고리 트리 리스트
         */
        draft.searchProductCategoryModal.data.productCategoryTreeList = productCategoryTreeList;
        /**
         *  상품 카테고리 전체 리스트
         */
        draft.searchProductCategoryModal.data.productCategoryAllList = productCategoryAllList;
      });
    },

    /**
     *  모달 토글
     */
    [actions.TOGGLE_MODAL]: (state, action) => {
      return produce(state, (draft) => {
        if (action.payload.data) {
          updateStoreWithPath([action.payload.name, 'data'], action.payload.data, draft);
        }

        updateStoreWithPath([action.payload.name, 'isOpen'], !state[action.payload.name].isOpen, draft);
      });
    },
  },
  initialState,
);
